/**
 * The option lists for an organization's preferences.
 *
 * A controlled `<select>` whose value matches no option renders blank, and the organization PUT
 * persists these fields, so a stored value outside the offered list shows an empty control over a
 * value the form is about to resend.
 *
 * The language list mirrors the `LanguageCode` enum in `backend/prisma/schema/enums.prisma`; the
 * frontend cannot import from the backend, so a test pins the mirror instead of the compiler.
 * `components/forms/LanguageSelect.tsx` is deliberately not reused: its codes (`en`, `es`) are a
 * different vocabulary from this enum (`en-US`, `es-MX`).
 */

export interface SelectOption {
  value: string;
  label: string;
}

export const ORGANIZATION_LANGUAGE_OPTIONS: readonly SelectOption[] = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'es-MX', label: 'Spanish (Mexico)' },
  { value: 'es-ES', label: 'Spanish (Spain)' },
  { value: 'fr-CA', label: 'French (Canada)' },
  { value: 'fr-FR', label: 'French (France)' },
  { value: 'pt-BR', label: 'Portuguese (Brazil)' },
  { value: 'de-DE', label: 'German (Germany)' },
  { value: 'it-IT', label: 'Italian (Italy)' },
  { value: 'zh-CN', label: 'Chinese (Simplified)' },
  { value: 'ja-JP', label: 'Japanese (Japan)' },
  { value: 'ko-KR', label: 'Korean (Korea)' },
];

// Timezones are NOT redefined here: `utils/timezone.ts` already exports TIMEZONE_OPTIONS. A second
// list would be one more pair to drift.

/**
 * The offered options plus the stored value when it is not among them, so a select never renders blank
 * over a value the organization actually holds. The extra option is labelled with the raw value: it is
 * more useful to the operator than an empty control, and it round-trips on save.
 */
export function withStoredOption(
  options: readonly SelectOption[],
  stored?: string | null
): SelectOption[] {
  const value = (stored || '').trim();
  if (!value || options.some((option) => option.value === value)) return [...options];
  return [...options, { value, label: value }];
}

export default { ORGANIZATION_LANGUAGE_OPTIONS, withStoredOption };
